/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import Imgix from 'js/components/Imgix';
import { LONG_MONTH_YEAR_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography2 } from '@coursera/cds-core';
import { Link } from '@coursera/event-pulse/react';
import type { EventData } from '@coursera/event-pulse/react';

import { getBadgePageUrl } from 'bundles/accomplishments/utils/routingUtils';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import SkillTags from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/SkillTags';
import getCredentialsItemStyles from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/getCredentialsItemStyles';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import type { LearnerProfile_IssuedBadgeFragment as IssuedBadge } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/LearnerProfileBadgeFragment';

import _t from 'i18n!nls/account-profile';

const styles = getCredentialsItemStyles(false);

const CredentialsSectionBadgeItem = ({ badge }: { badge: IssuedBadge }) => {
  const { sharedEventingV3Data } = useContext(ProfileContext);

  const badgeName = badge.badgeTemplate.title;
  const skills = badge.badgeTemplate.skills?.map((skill) => skill.skillName) ?? [];

  const badgePageUrl = getBadgePageUrl(badge.issuedBadgeId);
  const numCourses = badge.badgeTemplate.activities?.length ?? 0;
  const completionDateString = badge.issuedAt
    ? _t('#{numCourses} course series  •  Completed #{formattedCompletionDate}', {
        numCourses,
        formattedCompletionDate: formatDateTimeDisplay(badge.issuedAt, LONG_MONTH_YEAR_DISPLAY),
      })
    : '';

  const trackedBadge = { issuedBadgeId: badge.issuedBadgeId, badgeTemplateId: badge.badgeTemplate.badgeTemplateId };

  const trackingData: EventData<'click_button'> = {
    button: {
      name: EVENTS.openEducationHistoryBadgeItem,
      linkType: 'internal',
      linkURL: badgePageUrl,
    },
    product: {
      id: badge.badgeTemplate.badgeTemplateId,
    },
    badge: trackedBadge,
    ...sharedEventingV3Data,
  };

  return (
    <div css={styles.itemWrapper}>
      <div css={styles.iconWrapper}>
        <Imgix maxHeight={52} maxWidth={52} src={badge.badgeTemplate.imageUrl} alt={`${badgeName} logo`} />
      </div>
      <div css={styles.detailsWrapper}>
        <Typography2 component="h3" variant="subtitleMedium">
          {_t('#{badgeName} Learning Path', { badgeName })}
        </Typography2>
        <SkillTags skillNames={skills} />
        <Typography2 component="p" variant="bodySecondary">
          <Link trackingData={trackingData} href={badgePageUrl} variant="standard" target="_blank">
            {_t('View badge')}
          </Link>
        </Typography2>
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {completionDateString}
        </Typography2>
      </div>
    </div>
  );
};

export default CredentialsSectionBadgeItem;
